import {
  Button,
  DialogContent,
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  useId,
} from "@fluentui/react-components";
import { useBoolean } from "@fluentui/react-hooks";
import { CalendarRegular, DismissRegular } from "@fluentui/react-icons";
import { setSeconds } from "date-fns";
import { format, toZonedTime } from "date-fns-tz";
import { useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { LayoutContext } from "../../../context/LayoutContext";
import { $black, $sky60, $white } from "../../../cssjs/colors";
import { useApiCall } from "../../../hooks/useApiCall";
import { getAirportConfig } from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  EMPTY_STATE_MESSAGE,
} from "../../../utils/common/Constants";
import { getConvertedTime } from "../../../utils/common/DateHelperFunctions";
import { NoDataCard } from "../../common/no-data-card/NoDataCard";
import { default as CapacityChangeIcon } from "./../../../assests/flights/expand.svg";
import { getDates, getHoursList } from "./CapacityHelperFunction";

export default function CapacityChangePopup({
  runway,
  airportCode,
  refresh,
  setRefresh,
}: any) {
  const classes = useStyles();
  const [
    isCapacityChangeVisible,
    {
      setTrue: setIsCapacityChangeVisibleTrue,
      setFalse: setIsCapacityChangeVisibleFalse,
      toggle: toggleIsCapacityChangeCalloutVisible,
    },
  ] = useBoolean(false);
  const { timezone } = useContext(LayoutContext);
  const { timeZoneId: airportTimeZone } = getAirportConfig(airportCode);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [payload, setPayload] = useState<any>(null);
  const [slots, setSlots] = useState<{ time: string; capacity: string }[]>([]);
  const [dateOptions, setDateOptions] = useState<any[]>([]);
  const [resfreshData, setRefreshData] = useState<Date>(new Date());
  const capacityChangeButtonId = useId("capacity-button");
  const currentTime = setSeconds(
    getConvertedTime(new Date(), timezone, airportTimeZone),
    0
  );
  const now = new Date();
  // convert to airport timezone Date object
  const nowInAirportTz = toZonedTime(now, airportTimeZone);
  const todayDate = format(nowInAirportTz, "yyyy-MM-dd");

  const [selectedDate, setSelectedDate] = useState<any>(todayDate);

  const currentHour = `${currentTime
    .getHours()
    .toString()
    .padStart(2, "0")}:00`;
  const isToday = selectedDate === todayDate;
  const getRunwayCapacityUrl = `${process.env.REACT_APP_API_RUNWAY_CAPACITY?.replace(
    AIRPORT_CODE_PARAMETER,
    airportCode
  )
    .replace("{runway}", runway[0].replace("/", "%252F"))
    .replace("{date}", selectedDate)}`;
  const {
    data: runwayDataResponse,
    error: getRunwayError,
    isLoading: getRunwayIsLoading,
  } = useApiCall<any>({
    url: getRunwayCapacityUrl,
    dependencies: [
      selectedDate,
      airportCode,
      timezone,
      airportTimeZone,
      resfreshData,
    ],
  });

  const saveRunwayCapacityUrl = `${process.env.REACT_APP_API_SAVING_RUNWAY?.replace(
    AIRPORT_CODE_PARAMETER,
    airportCode
  )
    // To Be changed
    .replace("{runway}", runway[0].replace("/", "%252F"))
    .replace("{date}", selectedDate)}`;

  const [saveRunwayError, setSaveRunwayError] = useState<any>(null);
  const {
    data: saveRunwayCapacityResponse,
    error: apiSaveRunwayError,
    isLoading: saveRunwayIsLoading,
  } = useApiCall<any[]>({
    requestType: "put",
    url: saveRunwayCapacityUrl,
    payload,
    refresh: refresh,
    dependencies: [airportCode, payload],
  });

  useEffect(() => {
    setSaveRunwayError(apiSaveRunwayError);
  }, [apiSaveRunwayError]);

  useEffect(() => {
    if (isCapacityChangeVisible) {
      setSaveRunwayError(null);
    }
  }, [isCapacityChangeVisible]);
  const resetSlotsToApiData = () => {
    if (runwayDataResponse?.timeseriesData) {
      const visibleSlots = getHoursList(runwayDataResponse);
      setSlots(
        visibleSlots.map((s: any) => ({
          ...s,
          capacity: s.capacity !== undefined ? String(s.capacity) : "",
        }))
      );
    }
  };

  useEffect(() => {
    if (isCapacityChangeVisible) {
      resetSlotsToApiData();
    }
  }, [isCapacityChangeVisible, selectedDate, runwayDataResponse]);
  const viewTableContent =
    !getRunwayError &&
    !saveRunwayError &&
    !getRunwayIsLoading &&
    !saveRunwayIsLoading;

  const visibleSlots = runwayDataResponse?.timeseriesData
    ? getHoursList(runwayDataResponse)
    : [];

  useEffect(() => {
    if (runwayDataResponse?.timeseriesData) {
      setSlots(
        visibleSlots.map((s: any) => ({
          ...s,
          capacity: s.capacity !== undefined ? String(s.capacity) : "",
        }))
      );
    }
  }, [runwayDataResponse, selectedDate]);

  useEffect(() => {
    const dates = getDates(nowInAirportTz);
    setDateOptions(dates);
  }, []);

  useEffect(() => {
    if (saveRunwayCapacityResponse) {
      setRefreshData(new Date());
      setRefresh(false);
    }
  }, [saveRunwayCapacityResponse]);

  const handleCapacityChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length > 3) {
      value = value.slice(0, 3);
    }
    setSlots((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], capacity: value };
      return copy;
    });
  };

  const handleReset = () => {
    if (runwayDataResponse?.timeseriesData) {
      setSlots(
        visibleSlots.map((s: any) => ({
          ...s,
          capacity: s.capacity !== undefined ? String(s.capacity) : "",
        }))
      );
    }
  };

  const onSubmitHandler = async (data: any) => {
    const cleaned = data.map((slot: any) => ({
      ...slot,
      capacity: Number(slot.capacity) || 0,
    }));
    setPayload({ interval: 60, timeseriesData: cleaned });
    setRefresh(true);
  };

  const checkDisableField = (
    slotTime: string,
    selectedDate: string,
    airportTimeZone: string
  ) => {
    const now = new Date();
    const nowInAirportTz = toZonedTime(now, airportTimeZone);
    const currentAirportDate = format(nowInAirportTz, "yyyy-MM-dd");

    if (selectedDate < currentAirportDate) {
      return true;
    }
    if (selectedDate > currentAirportDate) {
      return false;
    }

    const currentHour = nowInAirportTz.getHours();
    const slotHour = parseInt(slotTime.split(":")[0], 10);

    return slotHour <= currentHour;
  };

  return (
    <div ref={containerRef}>
      <Popover
        open={isCapacityChangeVisible}
        onOpenChange={(e, data) => {
          if (data.open) {
            setSelectedDate(todayDate); // always reset on open
            if (!isCapacityChangeVisible) setIsCapacityChangeVisibleTrue();
          } else {
            if (isCapacityChangeVisible) setIsCapacityChangeVisibleFalse();
          }
        }}
        positioning="below-end"
        trapFocus={false}
      >
        <PopoverTrigger>
          <Button
            style={{ minWidth: "37px" }}
            onClick={() => {
              if (isCapacityChangeVisible) {
                setIsCapacityChangeVisibleFalse();
              } else {
                setSelectedDate(todayDate); // reset before showing
                setIsCapacityChangeVisibleTrue();
              }
            }}
            icon={
              <img
                src={CapacityChangeIcon}
                id={capacityChangeButtonId}
                alt="Hide unhide image"
                style={{ cursor: "pointer" }}
              />
            }
            iconPosition="after"
          />
        </PopoverTrigger>

        <PopoverSurface className={classes.popoverSurface} tabIndex={-1}>
          <div style={{ position: "relative" }}>
            <div className="amd-container-hspacebtwn d-flex ai-center">
              <div className="d-flex ai-center">
                <div className="fs-16 fw-600 ">RNW capacity ({runway})</div>
                <div className="ml-2">
                  <Select
                    value={{
                      value: selectedDate,
                      label: (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div>{selectedDate}</div>
                          <CalendarRegular
                            style={{ fontSize: "18px", color: $sky60 }}
                          />
                        </div>
                      ),
                    }}
                    options={dateOptions}
                    styles={customStyles}
                    isSearchable={false}
                    menuPosition="absolute"
                    menuShouldBlockScroll={true}
                    onChange={(selectedOption: any) => {
                      setSelectedDate(selectedOption?.value);
                    }}
                  />
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: "10px", right: "10px" }}>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={() => toggleIsCapacityChangeCalloutVisible()}
                className={classes.dismissButton}
              />
            </div>
            <div
              className={classes.subHeading}
              style={{ fontSize: "14px", marginTop: "5px" }}
            >
              By default, the system has a capacity of 46, which users can
              adjust via the input box.
            </div>
            <DialogContent>
              <div className={classes.popoverContainer}>
                {getRunwayIsLoading || saveRunwayIsLoading ? (
                  <Spinner
                    labelPosition="below"
                    label="Loading..."
                    className={classes.cardLoader}
                  />
                ) : null}
                {getRunwayError && (
                  <div style={{ marginTop: "125px" }}>
                    <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
                  </div>
                )}
                {saveRunwayError && (
                  <div style={{ marginTop: "125px" }}>
                    <NoDataCard message={EMPTY_STATE_MESSAGE.SaveFailed} />
                  </div>
                )}

                {viewTableContent ? (
                  <table className={classes.table}>
                    <thead>
                      <tr className={classes.tableHeader}>
                        <th className={classes.header}>Time Slot</th>
                        <th className={classes.header}>Capacity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slots?.map((slot: any, index: number) => (
                        <tr
                          key={`${slot.time}-${index}`}
                          style={{ borderBottom: "1px solid #ccc" }}
                        >
                          <td
                            className={classes.header}
                            style={{
                              width: "50%",
                            }}
                          >
                            {isToday && slot.time === currentHour ? (
                              <div
                                className={classes.nowTime}
                              >{`${slot.time} (now)`}</div>
                            ) : (
                              <div>{`${slot.time}`}</div>
                            )}
                          </td>
                          <td className={classes.header}>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              value={slot.capacity}
                              onChange={(e) =>
                                handleCapacityChange(index, e.target.value)
                              }
                              className={classes.inputField}
                              //disabled={checkDisableField(slot)}
                              disabled={checkDisableField(
                                slot.time,
                                selectedDate,
                                airportTimeZone
                              )}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  ""
                )}
              </div>
            </DialogContent>
            {viewTableContent && (
              <footer className="pt-sm-4 amd-container-vcenter jc-spacebtwn mb-2">
                <div>
                  <Button
                    className={mergeClasses(classes.resetButton, "mr-1")}
                    size="small"
                    onClick={() => {
                      handleReset();
                    }}
                  >
                    Reset
                  </Button>
                </div>
                <div>
                  <Button
                    size="small"
                    className={mergeClasses(classes.saveButton, "mr-1")}
                    onClick={() => {
                      onSubmitHandler(slots);
                    }}
                  >
                    Save
                  </Button>
                </div>
              </footer>
            )}
          </div>
        </PopoverSurface>
      </Popover>
    </div>
  );
}
const customStyles = {
  control: (base: any) => ({
    ...base,
    minHeight: "3px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: "500",
    maxHeight: "35px",
    width: "155px",
  }),
};
const useStyles = makeStyles({
  cardLoader: {
    height: "100%",
  },
  resetButton: {
    border: `1px solid ${$black}`,
    borderRadius: "3px",
    padding: " 5px",
    fontWeight: "600",
    fontSize: "13px",
    ":hover": {
      backgroundColor: $sky60,
      color: $white,
    },
  },
  saveButton: {
    color: $sky60,
    padding: " 5px 10px",
    borderRadius: "3px",
    border: `1px solid ${$sky60}`,
    fontWeight: "600",
    fontSize: "13px",
    ":hover": {
      backgroundColor: $sky60,
      color: $white,
    },
  },
  applyFilterButton: {
    color: "#0C66E1",
    borderRadius: "3px",
    fontWeight: "600",
    fontSize: "13px",
    border: `1px solid ${$sky60}`,
    padding: "5px 18px",
    ":hover": {
      backgroundColor: $sky60,
      color: $white,
    },
  },
  tableHeader: {
    borderTop: `2px solid rgb(204, 204, 204)`,
    borderBottom: "2px solid rgb(204, 204, 204)",
  },
  inputField: {
    width: "50px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    borderRadius: "4px",
    padding: "4px",
  },
  popoverContainer: {
    maxWidth: "400px",
    margin: "20px auto",
    height: "350px",
    maxHeight: "500px",
    minHeight: "350px",
  },
  popoverSurface: {
    minWidth: "300px",
    minHeight: "500px",
    maxWidth: "400px",
    maxHeight: "550px",
    zIndex: 9999,
    position: "relative",
  },
  dismissButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  header: { textAlign: "center", padding: "8px" },
  table: { width: "100%", borderCollapse: "collapse" },
  subHeading: { fontSize: "14px", marginTop: "5px" },
  nowTime: {
    textAlign: "left",
    paddingLeft: "41%",
    fontWeight: "bold",
  },
});
